import {action} from 'mobx';
import get from 'lodash/get';
import {getRootStore} from '../../../../../app.bootstrap';
import {ITreeNode} from '@techsee/techsee-ui-common/lib/simple-tree-control-view';
import filter from 'lodash/filter';
import {STORAGE_TYPES, RESOURCE_TYPES} from '../../../../../../states/dashboard/main/main.settings';
import forEach from 'lodash/forEach';
import {LIBRARY_DIR_DEPTH} from '@techsee/techsee-common/lib/constants/utils.constant';
import findIndex from 'lodash/findIndex';
import {IResourcesService, IResourceData} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {EventLogService} from '../../../../../services/event-log/event-log.service';
import reduce from 'lodash/reduce';
import {uuidv4} from '@techsee/techsee-common/lib/core/uuid';
import {BaseTreeController} from '../../../BaseTreeController';

interface IResourceTypes {
    [key: string]: string;
}

export enum LibraryTreeEvents {
    SELECT_TOOL = 'selectTool',
    SET_RESOURCE = 'setResource',
    OPEN_LIBRARY_DIALOG = 'openLibraryDialog',
    SET_PRELOADED_RESOURCE = 'setPreloadedResource',
    LOAD_TEXT_MESSAGE = 'loadTextMessage',
    SET_MSG_SECTION_OPEN = 'setMsgSectionOpen',
    SET_HIDE_WIDGETS = 'setHideWidgets',
    SET_HIDE_WIFI_SCAN = 'setHideWifiScan',
    HIDE_WIFI_ANALYZER_SCAN = 'hideWifiAnalyzerScan',
    SEND_TEXT = 'sendText'
}

export class LibraryTreeController extends BaseTreeController<LibraryTreeEvents> {
    private resourceTypes: IResourceTypes;

    constructor(
        accountId: string,
        userId: string,
        owners: string,
        private isLibraryTaggingMode: boolean,
        resourceService: IResourcesService,
        eventLog: EventLogService,
        trace: any,
        useNewInterface: boolean
    ) {
        super(accountId, userId, owners, resourceService, eventLog, trace, useNewInterface);

        this._treeNode.data.title = 'library';

        this.resourceTypes = reduce(
            RESOURCE_TYPES,
            (resourceTypes: IResourceTypes, value: {type: string; display: string; icon: string}) => {
                resourceTypes[value.type.toLowerCase()] = value.type;

                return resourceTypes;
            },
            {} as IResourceTypes
        );

        this._loadLibrary();
    }

    @action
    selectNode = (node: ITreeNode): void => {
        if (node && node.data.type !== 'dir') {
            this.libraryFileSelected(node);
        } else if (node) {
            this.emit(LibraryTreeEvents.OPEN_LIBRARY_DIALOG, node);
        }
    };

    @action
    private libraryFileSelected(node: ITreeNode): void {
        // Unselect the previous node if it exists
        if (this.selectedNode) {
            this.selectedNode.state.selected = false;
        }

        // Select the new node
        if (node.state) {
            node.state.selected = true;
        }

        Promise.resolve()
            .then(() => {
                if (get(node.data.resource, 'security.enabled')) {
                    return this.resourceService.resign(node.data.resource._id).then((resp: any) => {
                        node.data.resource = get(resp, 'data', resp);
                        return node;
                    });
                }
                return node;
            })
            .then((updatedNode) => {
                if (updatedNode.data.type === 'img') {
                    this.emit(LibraryTreeEvents.SELECT_TOOL, 'arrow', true);
                    this.emit(
                        LibraryTreeEvents.SET_RESOURCE,
                        updatedNode.data.resource ? updatedNode.data.resource : updatedNode.data
                    );
                    this.emit(LibraryTreeEvents.SET_HIDE_WIDGETS, false);
                    this.emit(LibraryTreeEvents.SET_HIDE_WIFI_SCAN, true);
                    this.emit(LibraryTreeEvents.HIDE_WIFI_ANALYZER_SCAN);

                    if (this.isLibraryTaggingMode && updatedNode.data.resource) {
                        this.emit(LibraryTreeEvents.SET_PRELOADED_RESOURCE, updatedNode.data.resource);
                    }

                    this.selectedNode;

                    return;
                }

                if (updatedNode.data.type === 'file') {
                    this.selectedNode;

                    const chatApi = getRootStore().chatApi;

                    chatApi.sendText(updatedNode.data.resource.url, {file: true});

                    return;
                }

                this.emit(LibraryTreeEvents.LOAD_TEXT_MESSAGE, updatedNode.data.resource.url);
                this.emit(LibraryTreeEvents.SET_MSG_SECTION_OPEN, true);
            })
            .catch((err) => {
                console.error('Error selecting library file:', err);
            });
    }

    private _getLocation(href: string) {
        const match = href.match(/^(https?:)\/\/(([^:\/?#]*)(?::([0-9]+))?)(\/[^?#]*)(\?[^#]*|)(#.*|)$/);

        return (
            match && {
                protocol: match[1],
                host: match[2],
                hostname: match[3],
                port: match[4],
                pathname: match[5],
                search: match[6],
                hash: match[7]
            }
        );
    }

    private _splitPath(pathname: string, depth: number): string[] {
        return pathname
            .slice(1, pathname.lastIndexOf('/'))
            .split('/')
            .slice(depth)
            .map((component) => {
                try {
                    return decodeURIComponent(component);
                } catch (e) {
                    return component;
                }
            });
    }

    private _isCloudfrontUrl(url: string): boolean {
        return /Key-Pair-Id/.test(url);
    }

    @action
    private _listToTree(rootName: string, resources: IResourceData[]): void {
        // Each node has an array of children, which also contains nodes
        // and an array of parents which contains string names of parents,
        // used for lookups, from the root of the tree.
        const lib: ITreeNode = {
            id: uuidv4(),
            parents: [],
            children: [],
            state: {expanded: true, selected: false, disabled: false},
            data: {title: rootName, type: 'dir'}
        };

        const libraryResources = filter(
            resources,
            (resource) =>
                (resource.type === this.resourceTypes.image || resource.type === this.resourceTypes.pdf) &&
                resource.storageType !== STORAGE_TYPES.ext
        );

        forEach(libraryResources, (resource) => {
            const url = this._getLocation(resource.url);

            if (!url) {
                return;
            }

            const type = resource.type === this.resourceTypes.image ? 'img' : 'file',
                depth = this._isCloudfrontUrl(resource.url) ? LIBRARY_DIR_DEPTH - 1 : LIBRARY_DIR_DEPTH,
                path = this._splitPath(url.pathname, depth);

            // Handles files in the top-level directory
            if (url.pathname.lastIndexOf('/') <= 0) {
                lib.children?.push({
                    id: uuidv4(),
                    parents: [],
                    children: [],
                    state: {expanded: false, selected: false, disabled: false},
                    data: {title: resource.title, type: type, url: resource.url, resource: resource}
                });
            } else {
                let lastDir = lib;

                forEach(path, (currentPath, key) => {
                    let index = findIndex(
                        lastDir.children,
                        (child) => child.data.title === currentPath && child.data.type === 'dir'
                    );

                    if (index < 0) {
                        lastDir.children?.push({
                            id: uuidv4(),
                            parents: path.slice(0, key),
                            children: [],
                            state: {expanded: false, selected: false, disabled: false},
                            data: {title: currentPath, type: 'dir'}
                        });
                        index = (lastDir.children?.length || 1) - 1;
                    }
                    lastDir = lastDir.children?.[index] || lastDir;
                });

                lastDir.children?.push({
                    id: uuidv4(),
                    parents: path,
                    children: [],
                    state: {expanded: false, selected: false, disabled: false},
                    data: {title: resource.title, type: type, url: resource.url, resource: resource}
                });
            }
        });

        this._treeNode = lib;
    }

    @action
    private _loadLibrary = (): Promise<void> | void => {
        if (this.accountId && this.resourceService) {
            return this.resourceService
                .findAll({
                    accountId: this.accountId
                })
                .then((library) => {
                    this._listToTree('library', library);

                    this.trace.info(
                        `Library loading success - currentUser: ${this.userId}, library array size: ${library.length}`
                    );
                })
                .catch((err) => {
                    this.eventLog.libraryLoadingFailed({
                        currentUserId: this.userId,
                        error: err?.message || JSON.stringify(err).slice(0, 1000)
                    });
                });
        }
    };
}
